import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import type { LucideIcon } from "lucide-react";
import { RotateCcw, Shield, ShieldAlert, ShieldX } from "lucide-react";
import type { SlideProps } from "@/engine/types";
import { AnimatedCounter, GradientText, RevealItem, RevealList, SlideShell } from "@/ui";
import type { Hat, HatCard, Risk } from "./components/hatCards";
import { HAT_CARDS } from "./components/hatCards";

const EASE = [0.32, 0.72, 0, 1] as const;

interface HatStyle {
  label: string;
  icon: LucideIcon;
  pill: string;
  face: string;
  tallyColor: string;
  glow?: string;
}

const HAT_STYLE: Record<Hat, HatStyle> = {
  white: {
    label: "White hat",
    icon: Shield,
    pill: "border-accent-emerald/40 bg-accent-emerald/15 text-accent-emerald",
    face: "border-accent-emerald/35 bg-ink-900/90 bg-linear-to-b from-accent-emerald/15 via-transparent to-transparent",
    tallyColor: "text-accent-emerald",
  },
  grey: {
    label: "Grey hat",
    icon: ShieldAlert,
    pill: "border-accent-amber/40 bg-accent-amber/15 text-accent-amber",
    face: "border-accent-amber/35 bg-ink-900/90 bg-linear-to-b from-accent-amber/15 via-transparent to-transparent",
    tallyColor: "text-accent-amber",
  },
  black: {
    label: "Black hat",
    icon: ShieldX,
    pill: "border-accent-rose/40 bg-accent-rose/15 text-accent-rose",
    face: "border-accent-rose/45 bg-ink-950/95 bg-linear-to-b from-accent-rose/10 via-transparent to-transparent",
    tallyColor: "text-accent-rose",
    glow: "0 0 36px -4px rgba(251, 113, 133, 0.45)",
  },
};

const RISK_STYLE: Record<Risk, { lit: number; color: string; label: string }> = {
  none: { lit: 1, color: "#34d399", label: "None" },
  medium: { lit: 2, color: "#fbbf24", label: "Medium" },
  high: { lit: 3, color: "#fb923c", label: "High" },
  severe: { lit: 4, color: "#fb7185", label: "Severe" },
};

function RiskMeter({ risk }: { risk: Risk }) {
  const spec = RISK_STYLE[risk];
  return (
    <div className="flex items-center gap-3">
      <span className="shrink-0 text-xl text-white/50">Risk</span>
      <div className="flex flex-1 gap-1.5">
        {[0, 1, 2, 3].map((i) => {
          const lit = i < spec.lit;
          const pulse = lit && risk === "severe" && i === 3;
          return (
            <div
              key={i}
              className={`h-2.5 flex-1 rounded-full ${pulse ? "animate-pulse" : ""}`}
              style={
                lit
                  ? { backgroundColor: spec.color, boxShadow: `0 0 10px ${spec.color}66` }
                  : { backgroundColor: "rgba(255,255,255,0.10)" }
              }
            />
          );
        })}
      </div>
      <span className="shrink-0 text-xl font-semibold" style={{ color: spec.color }}>
        {spec.label}
      </span>
    </div>
  );
}

function FlipCard({
  card,
  flipped,
  onFlip,
}: {
  card: HatCard;
  flipped: boolean;
  onFlip: () => void;
}) {
  const hat = HAT_STYLE[card.hat];
  const TacticIcon = card.icon;
  const HatIcon = hat.icon;
  return (
    <div className="h-full [perspective:1400px]">
      <motion.div
        className="relative h-full w-full [transform-style:preserve-3d]"
        animate={{ rotateY: flipped ? 180 : 0 }}
        transition={{ duration: 0.65, ease: EASE }}
      >
        {/* Front — the tactic */}
        <button
          type="button"
          onClick={onFlip}
          disabled={flipped}
          className={`glass absolute inset-0 flex flex-col items-center justify-center gap-2.5 p-4 text-center transition-[border-color,background-color,transform] duration-200 [backface-visibility:hidden] ${
            flipped ? "" : "cursor-pointer hover:scale-[1.02] hover:border-white/25 hover:bg-white/10"
          }`}
        >
          <TacticIcon size={30} className="shrink-0 text-white/60" strokeWidth={1.75} />
          <span className="text-2xl leading-snug text-white/90">{card.tactic}</span>
        </button>
        {/* Back — the verdict */}
        <div
          className={`absolute inset-0 flex flex-col justify-between gap-2 rounded-2xl border p-5 [backface-visibility:hidden] [transform:rotateY(180deg)] ${hat.face}`}
          style={hat.glow ? { boxShadow: hat.glow } : undefined}
        >
          <div className="flex items-center justify-between gap-2">
            <span
              className={`inline-flex items-center gap-2 rounded-full border px-4 py-1 text-xl font-semibold uppercase tracking-wider ${hat.pill}`}
            >
              <HatIcon size={20} strokeWidth={2} />
              {hat.label}
            </span>
            {card.twist && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-accent-fuchsia/50 bg-accent-fuchsia/15 px-3 py-1 text-xl font-semibold text-accent-fuchsia">
                ★ Twist
              </span>
            )}
          </div>
          <p className="text-left text-xl leading-snug text-white/85">{card.why}</p>
          <RiskMeter risk={card.risk} />
        </div>
      </motion.div>
    </div>
  );
}

/** c6-hat-sorting — DEMO: sort the tactics into hats. */
export function HatSortingSlide({ active }: SlideProps) {
  const [flipped, setFlipped] = useState<ReadonlySet<string>>(new Set());

  const counts = useMemo(() => {
    const tally = { white: 0, grey: 0, black: 0 };
    for (const card of HAT_CARDS) {
      if (flipped.has(card.id)) tally[card.hat] += 1;
    }
    return tally;
  }, [flipped]);

  const allFlipped = flipped.size === HAT_CARDS.length;

  return (
    <SlideShell chapter={6} kicker="Demo · Ask the room, then click to reveal">
      <div className="flex h-full min-h-0 w-full flex-col gap-5">
        <RevealList
          active={active}
          stagger={0.05}
          className="grid min-h-0 flex-1 grid-cols-4 grid-rows-3 gap-4"
        >
          {HAT_CARDS.map((card) => (
            <RevealItem key={card.id} className="min-h-0">
              <FlipCard
                card={card}
                flipped={flipped.has(card.id)}
                onFlip={() => setFlipped((prev) => new Set(prev).add(card.id))}
              />
            </RevealItem>
          ))}
        </RevealList>

        <div className="glass relative shrink-0 px-10 py-4">
          <AnimatePresence mode="wait" initial={false}>
            {allFlipped ? (
              <motion.div
                key="closing"
                initial={{ opacity: 0, y: 16, filter: "blur(6px)" }}
                animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.5, ease: EASE }}
                className="flex items-center justify-between gap-8"
              >
                <p className="font-display text-3xl font-semibold text-white">
                  White hat is slower.{" "}
                  <GradientText from="#34d399" to="#22d3ee">
                    It's also sustainable — for real brands and teams.
                  </GradientText>
                </p>
                <button
                  type="button"
                  onClick={() => setFlipped(new Set())}
                  className="inline-flex shrink-0 cursor-pointer items-center gap-2.5 rounded-full border border-white/15 bg-white/5 px-6 py-2.5 text-xl font-medium text-white/80 transition-all duration-200 hover:scale-105 hover:border-white/30 hover:bg-white/10 hover:text-white"
                >
                  <RotateCcw size={20} />
                  Reset
                </button>
              </motion.div>
            ) : (
              <motion.div
                key="tally"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="flex items-center justify-center gap-8"
              >
                {(["white", "grey", "black"] as const).map((hat, i) => {
                  const style = HAT_STYLE[hat];
                  const HatIcon = style.icon;
                  return (
                    <span key={hat} className="flex items-center gap-8">
                      {i > 0 && <span className="text-2xl text-white/25">·</span>}
                      <span className="flex items-center gap-3">
                        <HatIcon size={26} className={style.tallyColor} strokeWidth={2} />
                        <span className="text-2xl text-white/70">{style.label}</span>
                        <AnimatedCounter
                          to={counts[hat]}
                          active={active}
                          duration={0.5}
                          className={`font-display text-3xl font-semibold ${style.tallyColor}`}
                        />
                      </span>
                    </span>
                  );
                })}
                <span className="ml-auto text-xl text-white/40">
                  {flipped.size} / {HAT_CARDS.length} revealed
                </span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </SlideShell>
  );
}
